import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Data, ErrorResponse } from '../model/store.models';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { StoreProductDetails } from '../model/store-product-details.model';
import { StoreProductCheckout } from '../model/store-product-checkout.model';


@Injectable({
  providedIn: 'root'
})
export class ShopifyService {

  client: any;

  constructor() {
    this.client = createStorefrontApiClient({
      storeDomain: environment.url,
      apiVersion: '2025-01',
      publicAccessToken: environment.shopifyaccesstoken,
    });
  }

  async getCollectionByHandle(handle: string): Promise<Data | ErrorResponse> {
    const query = `
      query CollectionByHandle($handle: String!) {
        collectionByHandle(handle: $handle) {
          id
          title
          description
          seo { title description }
          metafields(identifiers: [
            {namespace: "custom", key: "hero_content"},
            {namespace: "custom", key: "page_content"}
          ]) {
            key
            value
          }
          products(first: 100) {
            edges {
              node {
                id
                title
                handle
                description
                featuredImage {
                  altText
                  url
                }
                variants(first: 1) {
                  edges {
                    node {
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const variables = { handle };
      const { data } = await this.client.request(query, { variables });
      return data;
    } catch (error) {
      return {
        errors: {
          networkStatusCode: error.response.status,
          message: error.message,
        },
      };
    }
  }

  async getProductDetailsByHandle(handle: string): Promise<StoreProductDetails | ErrorResponse> {
    const query = `
      query {
        product(handle: "${handle}") {
          id
          title
          description
          handle
          metafields(identifiers: [
            {namespace: "custom", key: "hero_content"},
            {namespace: "custom", key: "hint_premium_offer"},
            {namespace: "custom", key: "shipping_info"},
            {namespace: "custom", key: "features"}
          ]) {
            key
            value
          }
          metafield(namespace: "shopify", key: "case-color") {
              references(first: 5) {
              edges {
                node {
                  ... on Metaobject {
                    id
                    handle
                    fields {
                      key
                      value
                    }
                  }
                }
              }
            }
          }
          images(first: 25) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 25) {
            edges {
              node {
                  id
                  title
                  image {
                      altText
                      url
                  }
                  compareAtPrice {
                      amount
                      currencyCode
                  }
                  price {
                      amount
                      currencyCode
                  }
              }
            }
          }
        }
      }
    `;

    try {
      const variables = { handle };
      const { data } = await this.client.request(query, { variables });
      return data;
    } catch (error) {
      return {
        errors: {
          networkStatusCode: error.response.status,
          message: error.message,
        },
      };
    }
  }

  async checkout(variantId: string): Promise<StoreProductCheckout | ErrorResponse> {
    const query = `
      mutation {
        cartCreate(
          input: {
            lines: [
              {
                quantity: 1,
                merchandiseId: "${variantId}"
              },
              {
                quantity: 1,
                merchandiseId: "${environment.hintPremiumVariantId}"
              }
            ],
          }
        ) {
          cart {
            id
            createdAt
            updatedAt
            checkoutUrl
            lines(first: 2) {
              edges {
                node {
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                    }
                  }
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
              totalDutyAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    try {
      const variables = { variantId };
      const { data } = await this.client.request(query, { variables });
      return data;
    } catch (error) {
      return {
        errors: {
          networkStatusCode: error.response.status,
          message: error.message,
        },
      };
    }
  }
}
